import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConnectionStatus } from '../../types/connections';
import { Pipeline } from '../../types/pipelines';
import { apiFetch } from '../../services/api';
import { PipelineFormLayout } from '../PipelineFormLayout';

interface GoogleAdsAccount {
    id: string;
    displayId: string;
    name?: string | null;
}

// Normalizes "6970469076" -> "697-046-9076"; keeps anything else as typed
const formatAccountId = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return value;
};

const isValidAccountId = (value: string): boolean =>
    /^\d{3}-\d{3}-\d{4}$/.test(value);

const GoogleAdsCostLoaderForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = !!id;

    const [pipelineName, setPipelineName] = useState('');
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);

    // Google Ads State
    const [selectedGadsConnection, setSelectedGadsConnection] = useState<string>('');
    const [gadsAccounts, setGadsAccounts] = useState<GoogleAdsAccount[]>([]);
    const [accountsAvailable, setAccountsAvailable] = useState<boolean>(false);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [googleAdsAccountId, setGoogleAdsAccountId] = useState<string>('');

    // GBQ destination State
    const [selectedGBQConnection, setSelectedGBQConnection] = useState<string>('');
    const [bqProjects, setBqProjects] = useState<{ id: string; name: string }[]>([]);
    const [selectedProject, setSelectedProject] = useState<string>('');
    const [datasets, setDatasets] = useState<string[]>([]);
    const [selectedDataset, setSelectedDataset] = useState<string>('');
    const [loadingBQ, setLoadingBQ] = useState(false);
    const [loadingDatasets, setLoadingDatasets] = useState(false);
    const [bqError, setBqError] = useState<string | null>(null);

    useEffect(() => {
        loadConnectionStatus();
        if (isEditing) {
            loadPipeline(id!);
        }
    }, [id, isEditing]);

    const loadConnectionStatus = async () => {
        try {
            const res = await apiFetch('/api/connections/status');
            if (res.ok) {
                setConnectionStatus(await res.json());
            }
        } catch (error) {
            console.error('Failed to load connection status:', error);
        }
    };

    const loadPipeline = async (pipelineId: string) => {
        try {
            const res = await apiFetch(`/api/pipelines/${pipelineId}`);
            if (res.ok) {
                const pipeline: Pipeline = await res.json();
                setPipelineName(pipeline.name);
                if (pipeline.connections?.gadsConnectionId) setSelectedGadsConnection(pipeline.connections.gadsConnectionId);
                if (pipeline.connections?.gbqConnectionId) setSelectedGBQConnection(pipeline.connections.gbqConnectionId);
                if (pipeline.parameters?.googleAdsAccountId) setGoogleAdsAccountId(pipeline.parameters.googleAdsAccountId);
                if (pipeline.dataSources?.google_ads_cost?.project) setSelectedProject(pipeline.dataSources.google_ads_cost.project);
                if (pipeline.dataSources?.google_ads_cost?.dataset) setSelectedDataset(pipeline.dataSources.google_ads_cost.dataset);
            }
        } catch (error) {
            console.error('Failed to load pipeline:', error);
        }
    };

    // Try to load available Google Ads accounts; fall back to manual entry
    const fetchGadsAccounts = useCallback(async () => {
        if (!selectedGadsConnection) return;
        setLoadingAccounts(true);
        setAccountsAvailable(false);
        setGadsAccounts([]);
        try {
            const res = await apiFetch(`/api/connections/gads/accounts?gadsConnectionId=${selectedGadsConnection}`);
            if (res.ok) {
                const accounts: GoogleAdsAccount[] = await res.json();
                if (accounts.length > 0) {
                    setGadsAccounts(accounts);
                    setAccountsAvailable(true);
                }
            }
            // Any non-OK response (501 not configured, 502 API error, etc.)
            // silently falls back to manual account ID input
        } catch (error) {
            console.error('Failed to load Google Ads accounts:', error);
        } finally {
            setLoadingAccounts(false);
        }
    }, [selectedGadsConnection]);

    useEffect(() => {
        if (selectedGadsConnection) {
            fetchGadsAccounts();
        }
    }, [selectedGadsConnection, fetchGadsAccounts]);

    const fetchBQProjects = useCallback(async () => {
        if (!selectedGBQConnection) return;
        setLoadingBQ(true);
        setBqError(null);
        try {
            const response = await apiFetch(`/api/connections/bigquery/projects?gbqConnectionId=${selectedGBQConnection}`);
            if (!response.ok) throw new Error('Failed to fetch projects');
            const data = await response.json();
            const projects = Array.isArray(data) ? data.map((p: any) =>
                typeof p === 'string' ? { id: p, name: p } : p
            ) : [];
            setBqProjects(projects);
        } catch (error: any) {
            setBqError(error.message);
        } finally {
            setLoadingBQ(false);
        }
    }, [selectedGBQConnection]);

    useEffect(() => {
        if (selectedGBQConnection) {
            fetchBQProjects();
        }
    }, [selectedGBQConnection, fetchBQProjects]);

    const fetchDatasets = useCallback(async () => {
        if (!selectedGBQConnection || !selectedProject) return;
        setLoadingDatasets(true);
        try {
            const response = await apiFetch(`/api/connections/bigquery/datasets?projectId=${selectedProject}&gbqConnectionId=${selectedGBQConnection}`);
            if (!response.ok) throw new Error('Failed to fetch datasets');
            const data = await response.json();
            setDatasets(Array.isArray(data) ? data : []);
        } catch (error: any) {
            console.error('Error fetching datasets:', error);
        } finally {
            setLoadingDatasets(false);
        }
    }, [selectedGBQConnection, selectedProject]);

    useEffect(() => {
        if (selectedProject && selectedGBQConnection) {
            fetchDatasets();
        }
    }, [selectedProject, selectedGBQConnection, fetchDatasets]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isValidAccountId(googleAdsAccountId)) {
            alert('Please provide a Google Ads Account ID in the format 697-046-9076');
            return;
        }

        const pipelineData = {
            type: 'google-ads-cost-loader' as const,
            name: pipelineName || 'Google Ads Cost Loader Pipeline',
            connections: {
                gadsConnectionId: selectedGadsConnection,
                gbqConnectionId: selectedGBQConnection
            },
            dataSources: {
                google_ads_cost: {
                    project: selectedProject,
                    dataset: selectedDataset
                }
            },
            parameters: {
                googleAdsAccountId
            }
        };

        try {
            const method = isEditing ? 'PUT' : 'POST';
            const url = isEditing ? `/api/pipelines/${id}` : '/api/pipelines';

            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(pipelineData)
            });

            if (res.ok) {
                navigate('/pipelines');
            } else {
                throw new Error('Failed to save pipeline');
            }
        } catch (error) {
            console.error('Error saving pipeline:', error);
            alert('Failed to save pipeline');
        }
    };

    return (
        <PipelineFormLayout isEditing={isEditing} pipelineType="google-ads-cost-loader">
            <form onSubmit={handleSubmit}>
                {/* Google Ads Connector Block */}
                <div className="form-section">
                    <h3>Google Ads Connector</h3>
                    <div className="form-group">
                        <label className="required">Google Ads Connection</label>
                        <select
                            value={selectedGadsConnection}
                            onChange={e => {
                                setSelectedGadsConnection(e.target.value);
                                setGoogleAdsAccountId('');
                            }}
                            required
                        >
                            <option value="">Select Connection</option>
                            {connectionStatus?.gads?.map(c => (
                                <option key={c.id} value={c.id}>{c.email}</option>
                            ))}
                        </select>
                        {(!connectionStatus?.gads || connectionStatus.gads.length === 0) && (
                            <small style={{ color: '#64748b' }}>
                                No Google Ads connections yet — add one in the Connections tab.
                            </small>
                        )}
                    </div>
                    {selectedGadsConnection && (
                        <div className="form-group">
                            <label className="required">Google Ads Account</label>
                            {loadingAccounts ? (
                                <select disabled>
                                    <option>Loading accounts...</option>
                                </select>
                            ) : accountsAvailable ? (
                                <select
                                    value={googleAdsAccountId}
                                    onChange={e => setGoogleAdsAccountId(e.target.value)}
                                    required
                                >
                                    <option value="">Select Account</option>
                                    {gadsAccounts.map(a => (
                                        <option key={a.id} value={a.displayId}>
                                            {a.name ? `${a.name} (${a.displayId})` : a.displayId}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <>
                                    <input
                                        type="text"
                                        value={googleAdsAccountId}
                                        onChange={e => setGoogleAdsAccountId(e.target.value)}
                                        onBlur={e => setGoogleAdsAccountId(formatAccountId(e.target.value))}
                                        placeholder="697-046-9076"
                                        pattern="\d{3}-\d{3}-\d{4}"
                                        title="Google Ads Account ID in the format 697-046-9076"
                                        required
                                    />
                                    <small style={{ color: '#64748b' }}>
                                        Could not load accounts automatically — enter the Google Ads Account ID manually (format: 697-046-9076).
                                    </small>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* BigQuery destination Block */}
                <div className="form-section">
                    <h3>BigQuery Destination</h3>
                    <div className="form-group">
                        <label className="required">BigQuery Connection</label>
                        <select
                            value={selectedGBQConnection}
                            onChange={e => {
                                setSelectedGBQConnection(e.target.value);
                                setSelectedProject('');
                                setSelectedDataset('');
                            }}
                            required
                        >
                            <option value="">Select Connection</option>
                            {connectionStatus?.gbq?.map(c => (
                                <option key={c.id} value={c.id}>{c.email}</option>
                            ))}
                        </select>
                    </div>
                    {selectedGBQConnection && (
                        <>
                            {bqError && <div className="error-message">{bqError}</div>}
                            <div className="form-group">
                                <label className="required">Project</label>
                                <select
                                    value={selectedProject}
                                    onChange={e => {
                                        setSelectedProject(e.target.value);
                                        setSelectedDataset('');
                                        if (!pipelineName && e.target.value && bqProjects.length > 0) {
                                            const project = bqProjects.find(p => p.id === e.target.value);
                                            if (project) {
                                                setPipelineName(`Google Ads Cost - ${project.name}`);
                                            }
                                        }
                                    }}
                                    required
                                    disabled={loadingBQ}
                                >
                                    <option value="">{loadingBQ ? 'Loading...' : 'Select Project'}</option>
                                    {bqProjects.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                    ))}
                                </select>
                            </div>
                            {selectedProject && (
                                <div className="form-group">
                                    <label className="required">Dataset</label>
                                    <select
                                        value={selectedDataset}
                                        onChange={e => setSelectedDataset(e.target.value)}
                                        required
                                        disabled={loadingDatasets}
                                    >
                                        <option value="">{loadingDatasets ? 'Loading...' : 'Select Dataset'}</option>
                                        {datasets.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="form-section">
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            value={pipelineName}
                            onChange={e => setPipelineName(e.target.value)}
                            placeholder="Pipeline Name"
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button type="submit" className="primary-button">Save Pipeline</button>
                </div>
            </form>
        </PipelineFormLayout>
    );
};

export default GoogleAdsCostLoaderForm;
